import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { OFF } from '../../lib/off';
import { Link, useSearchParams } from 'react-router';
import { ALL_FEATURE_SETS, FEATURE_SETS, MAILBOX_FEATURE_SET } from '@stitch/pbx-adapter/catalog';
import { describeReferences, referencesTo, suggestNumber, type Reference } from '@stitch/pbx-adapter/describe';
import { companyLengthFrom, extensionHint, extensionProblem } from '@stitch/pbx-adapter/extensions';

type CompanyLength = ReturnType<typeof companyLengthFrom>;
import { api, type Intent, type PersonRow as Person } from '../../api/client';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { useLeland } from '../../lib/leland';
import { usePresence } from '../../components/Presence';
import { PresenceWithDndOff, useCanTurnOffDnd } from '../../components/DndOff';
import { usePictureOfExtension } from '../../lib/pictures';
import { Face } from '../../components/PhotoEditor';

// ---- Staged changes ---------------------------------------------------------------------------

/** timeZone: the phone system's own code for the person's time zone ('' for the default); not in the list of people. */
type Fields = { firstName: string; lastName: string; email: string; mobile: string; featureSet: string; timeZone?: string };
type Staged =
  | ({ kind: 'add'; extension: string } & Fields)
  | { kind: 'edit'; extension: string; name: string; set: Partial<Fields> }
  | { kind: 'remove'; extension: string; name: string }
  | { kind: 'welcome'; extension: string; name: string };

const LABEL: Record<keyof Fields, string> = { firstName: 'first name', lastName: 'last name', email: 'email', mobile: 'mobile', featureSet: 'feature set', timeZone: 'time zone' };
export const featureSetLabel = (v: string) => ALL_FEATURE_SETS.find((o) => o.value === v)?.label ?? v;
/** The phone system's names for its time zones, once loaded ("Eastern Time" for its code). */
export const pbxZoneNames = new Map<string, string>();
export const pbxZoneLabel = (v: string) => (v ? (pbxZoneNames.get(v) ?? v) : 'the company’s');

function describe(s: Staged): string {
  switch (s.kind) {
    case 'add':
      return `Add ${s.firstName} ${s.lastName} at extension ${s.extension}`;
    case 'edit':
      return `Change ${s.name} (${s.extension}): ${Object.entries(s.set)
        .map(([k, v]) => `${LABEL[k as keyof Fields]} → ${k === 'featureSet' ? featureSetLabel(v ?? '') : k === 'timeZone' ? pbxZoneLabel(v ?? '') : v || '(none)'}`)
        .join(', ')}`;
    case 'remove':
      return `Remove ${s.name} (${s.extension})`;
    case 'welcome':
      return `Email login details to ${s.name} (${s.extension})`;
  }
}

function toIntent(s: Staged): Intent {
  switch (s.kind) {
    case 'add':
      return {
        op: 'person.create',
        extension: s.extension,
        firstName: s.firstName,
        lastName: s.lastName,
        ...(s.email ? { email: s.email } : {}),
        ...(s.mobile ? { mobile: s.mobile } : {}),
        ...(s.featureSet ? { featureSet: s.featureSet } : {}),
        ...(s.timeZone ? { timeZone: s.timeZone } : {})
      };
    case 'edit':
      return { op: 'person.update', extension: s.extension, set: s.set };
    case 'remove':
      return { op: 'person.delete', extension: s.extension };
    case 'welcome':
      return { op: 'notify.welcome', extension: s.extension };
  }
}

export const fullName = (p: Pick<Fields, 'firstName' | 'lastName'>) => `${p.firstName} ${p.lastName}`.trim();
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9 ().-]{3,24}$/;

function validate(f: Fields & { extension?: string }, needExtension: boolean, company: CompanyLength = null): Partial<Record<keyof Fields | 'extension', string>> {
  const e: Partial<Record<keyof Fields | 'extension', string>> = {};
  // New extensions: 3 to 6 digits, as many as the company's others (2986).
  const problem = needExtension ? extensionProblem(f.extension ?? '', company) : null;
  if (problem) e.extension = problem;
  if (!f.firstName.trim()) e.firstName = 'Required.';
  if (!f.lastName.trim()) e.lastName = 'Required.';
  if (f.email.trim() && !EMAIL.test(f.email.trim())) e.email = 'Not a valid email address.';
  if (f.mobile.trim() && !PHONE.test(f.mobile.trim())) e.mobile = 'Not a valid phone number.';
  return e;
}

// ---- Page -------------------------------------------------------------------------------------

export function TenantPeople() {
  const { tenant, reload: reloadTenant } = useTenant();
  const people = useData(() => api.people(tenant.domain), [tenant.domain]);
  // Every number in the tenant (ring groups, queues...), so a suggested extension is never taken.
  const directory = useData(() => api.directory(tenant.domain).catch(() => null), [tenant.domain]);
  const presence = usePresence(tenant.domain);
  // What each person is used by (menu keys, voicemail destinations, ring groups, queues).
  const routing = useData(() => api.routing(tenant.domain).catch(() => null), [tenant.domain]);
  const refsOf = (ext: string): Reference[] =>
    referencesTo(ext, routing.data?.accounts ?? [], directory.data?.mailboxPrefix ? { mailboxPrefix: directory.data.mailboxPrefix } : {});
  const [staged, setStaged] = useState<Staged[]>([]);
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; person: Person } | { mode: 'remove'; person: Person } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  // Each opening gets a new key, so the dialog starts with the right values from its first frame.
  const [dialogKey, setDialogKey] = useState(0);
  const [reviewKey, setReviewKey] = useState(0);
  const openDialog = (d: NonNullable<typeof dialog>) => {
    setDialogKey((k) => k + 1);
    setDialog(d);
  };
  const [q, setQ] = useState('');
  const writable = tenant.writable;
  // Admins (and Stitch staff) can turn off someone's do not disturb from their status.
  const canDndOff = useCanTurnOffDnd(tenant.domain) && writable;

  useEffect(() => {
    if (params.get('add') && writable) {
      openDialog({ mode: 'add' });
      setParams({}, { replace: true });
    }
    // ?edit=201 and ?remove=201 (from a person's own page) open editing or removing them.
    const ext = params.get('edit') ?? params.get('remove');
    const who = ext ? people.data?.people.find((p) => p.extension === ext) : undefined;
    if (who && writable) {
      openDialog({ mode: params.get('edit') ? 'edit' : 'remove', person: who });
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams, writable, people.data]);

  // Voicemail boxes have their own page.
  const everyone = people.data?.people ?? [];
  const list = everyone.filter((p) => p.featureSet !== MAILBOX_FEATURE_SET);
  const boxes = everyone.length - list.length;
  // Shared phones (1.13.0, 3067): places, listed here with a filter, not counted as people.
  const places = useMemo(() => new Map((people.data?.sharedPhones ?? []).map((x) => [x.extension, x])), [people.data]);
  const [onlyPlaces, setOnlyPlaces] = useState(false);
  // Only offer fields this phone system can store (mobile numbers wait on the 69 field name).
  const hasMobile = people.data ? people.data.fields.includes('mobile') : false;
  // Feature sets show where the phone system has them; changing one may wait for confirmation.
  const featureSets: 'edit' | 'show' | 'hidden' = !people.data?.fields.includes('featureSet') ? 'hidden' : people.data.writableFields.includes('featureSet') ? 'edit' : 'show';
  // A person's own time zone: shown where the phone system has it; changing it may wait for confirmation.
  const timeZones: 'edit' | 'show' | 'hidden' = !people.data?.fields.includes('timeZone') ? 'hidden' : people.data.writableFields.includes('timeZone') ? 'edit' : 'show';
  // Removing extensions waits until the PBX's remove endpoint is verified.
  const canRemove = people.data?.canRemove ?? false;
  const byExt = useMemo(() => new Map(list.map((p) => [p.extension, p])), [list]);
  const stagedFor = (ext: string) => staged.filter((s) => s.extension === ext);
  const adds = staged.filter((s): s is Extract<Staged, { kind: 'add' }> => s.kind === 'add');
  // The extension after the last one used, counting people added in this session.
  // Voicemail boxes keep their numbers, but the next person follows the last person.
  const entries = [
    ...(directory.data?.entries ?? []).map((e) => (e.kind === 'person' && e.featureSet === MAILBOX_FEATURE_SET ? { ...e, kind: 'other' as const } : e)),
    ...list.map((p) => ({ extension: p.extension, kind: 'person' as const })),
    ...adds.map((a) => ({ extension: a.extension, kind: 'person' as const }))
  ];
  const company = companyLengthFrom(entries);
  const suggestedExtension = suggestNumber('person', entries, { ...(directory.data?.mailboxPrefix ? { mailboxPrefix: directory.data.mailboxPrefix } : {}), ...(company ? { length: company.length } : {}) });
  const lastExtension = entries
    .filter((e) => e.kind === 'person' && /^\d+$/.test(e.extension))
    .map((e) => Number(e.extension))
    .reduce((m, n) => Math.max(m, n), -1);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (p: { extension: string; firstName: string; lastName: string; email: string }) =>
      !needle || [p.extension, p.firstName, p.lastName, p.email].some((v) => v.toLowerCase().includes(needle));
    return list.filter(match).filter((p) => !onlyPlaces || places.has(p.extension));
  }, [list, q, onlyPlaces, places]);

  function stage(next: Staged) {
    setStaged((cur) => {
      const others = cur.filter((s) => !(s.extension === next.extension && s.kind === next.kind));
      // Removing someone cancels other pending changes to them.
      const cleaned = next.kind === 'remove' ? others.filter((s) => s.extension !== next.extension) : others;
      return [...cleaned, next];
    });
  }
  const unstage = (ext: string, kind?: Staged['kind']) => setStaged((cur) => cur.filter((s) => !(s.extension === ext && (!kind || s.kind === kind))));

  // Show staged edits in the table so people can see what will change.
  const shown = (p: Person): Fields => {
    const edit = stagedFor(p.extension).find((s) => s.kind === 'edit') as Extract<Staged, { kind: 'edit' }> | undefined;
    return { firstName: p.firstName, lastName: p.lastName, email: p.email, mobile: p.mobile, featureSet: p.featureSet, ...(edit?.set ?? {}) };
  };

  return (
    <>
      <PageHeader
        eyebrow={
          people.data
            ? `${plural(list.length - places.size, 'person', 'people')}${places.size ? ` · ${plural(places.size, 'shared phone', 'shared phones')}` : ''} · ${tenant.domain}`
            : tenant.domain
        }
        title="People"
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search people</span>
              <input type="search" placeholder="Search name, extension or email" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <ImportButton disabled={!writable} setupLink={`/t/${encodeURIComponent(tenant.domain)}/setup`} />
            <Button variant="ghost" disabled={!writable} onClick={() => openDialog({ mode: 'add' })} title="Add someone with just their name and extension">
              Quick add
            </Button>
            {writable && (
              <Link className="btn ghost" to={`/t/${encodeURIComponent(tenant.domain)}/people/new?place=1`} title="A lobby, kitchen or conference room phone: no person, no sign-in">
                Add a shared phone
              </Link>
            )}
            {writable ? (
              <Link className="btn primary" to={`/t/${encodeURIComponent(tenant.domain)}/people/new`}>
                <Icon name="plus" size={17} strokeWidth={2.2} /> Add person
              </Link>
            ) : (
              <Button variant="primary" icon="plus" disabled>
                Add person
              </Button>
            )}
          </>
        }
      />
      <div className="content">
        {!writable && (
          <Notice tone="warn" title="Read-only" icon="lock">
            {OFF.readOnly}
          </Notice>
        )}
        {writable && staged.length === 0 && (
          <Notice title="Nothing changes until you review and apply">
            Add, edit or remove people below. Your changes collect at the bottom of the screen; review them together, then apply. Every applied change can be undone for 30 days.
          </Notice>
        )}
        {places.size > 0 && (
          <div className="row-actions people-filter" role="group" aria-label="Show">
            <Button size="small" variant={onlyPlaces ? 'ghost' : 'secondary'} aria-pressed={!onlyPlaces} onClick={() => setOnlyPlaces(false)}>
              Everyone
            </Button>
            <Button size="small" variant={onlyPlaces ? 'secondary' : 'ghost'} aria-pressed={onlyPlaces} onClick={() => setOnlyPlaces(true)}>
              Shared phones ({places.size})
            </Button>
          </div>
        )}
        {boxes > 0 && (
          <Notice icon="mail">
            {plural(boxes, 'voicemail box', 'voicemail boxes')} (extensions with the Voicemail only feature set) {boxes === 1 ? 'is' : 'are'} on <Link to={`/t/${encodeURIComponent(tenant.domain)}/voicemail`}>Voicemail boxes</Link>.
          </Notice>
        )}
        <Card>
          {people.error ? (
            <LoadError error={people.error} retry={people.reload} />
          ) : !people.data ? (
            <Loading label="Loading people" />
          ) : list.length === 0 && adds.length === 0 ? (
            <Empty title="No people yet">Add the first person to give them an extension.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Ext</th>
                    <th scope="col">Name</th>
                    <th scope="col" className="hide-sm">
                      Email
                    </th>
                    {hasMobile && (
                      <th scope="col" className="hide-sm">
                        Mobile
                      </th>
                    )}
                    {featureSets !== 'hidden' && (
                      <th scope="col" className="hide-sm">
                        Feature set
                      </th>
                    )}
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adds.map((a) => (
                    <tr key={`add-${a.extension}`} className="staged">
                      <td className="mono">{a.extension}</td>
                      <td>
                        <span className="cell-stack">
                          <span>
                            {fullName(a)} <Chip small tone="brand">New</Chip>{' '}
                            {stagedFor(a.extension).some((s) => s.kind === 'welcome') && <Chip small>Login email</Chip>}
                          </span>
                        </span>
                      </td>
                      <td className="hide-sm">{a.email || <span className="muted">–</span>}</td>
                      {hasMobile && <td className="hide-sm mono">{a.mobile || <span className="muted">–</span>}</td>}
                      {featureSets !== 'hidden' && (
                        <td className="hide-sm">
                          <FeatureSetChip value={a.featureSet} />
                        </td>
                      )}
                      <td>
                        <div className="row-actions">
                          <button type="button" className="icon-btn" aria-label={`Cancel adding ${fullName(a)}`} title="Cancel" onClick={() => unstage(a.extension)}>
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.map((p) => {
                    const mine = stagedFor(p.extension);
                    const removing = mine.some((s) => s.kind === 'remove');
                    const editing = mine.some((s) => s.kind === 'edit');
                    const welcoming = mine.some((s) => s.kind === 'welcome');
                    const f = shown(p);
                    return (
                      <tr key={p.extension} className={removing ? 'staged removed' : mine.length ? 'staged' : ''}>
                        <td className="mono">{p.extension}</td>
                        <td>
                          <span className="person-cell">
                          <ExtensionFace tenant={tenant.domain} extension={p.extension} name={fullName(f)} />
                          <span className="cell-stack">
                            <span>
                              <Link className="person-link" to={`/t/${encodeURIComponent(tenant.domain)}/people/${encodeURIComponent(p.extension)}`}>
                                {fullName(f)}
                              </Link>{' '}
                              {removing && <Chip small tone="crit">Removing</Chip>} {editing && <Chip small tone="brand">Edited</Chip>}{' '}
                              {welcoming && <Chip small>Login email</Chip>}
                              {places.has(p.extension) && <Chip small>Shared phone</Chip>}
                            </span>
                            <PresenceWithDndOff
                              domain={tenant.domain}
                              extension={p.extension}
                              name={fullName(f)}
                              p={presence?.people[p.extension]}
                              dir={directory.data ?? undefined}
                              canTurnOff={canDndOff}
                            />
                          </span>
                          </span>
                        </td>
                        <td className="hide-sm">{f.email || <span className="muted">–</span>}</td>
                        {hasMobile && <td className="hide-sm mono">{f.mobile || <span className="muted">–</span>}</td>}
                        {featureSets !== 'hidden' && (
                          <td className="hide-sm">
                            <FeatureSetChip value={f.featureSet} />
                          </td>
                        )}
                        <td>
                          <div className="row-actions">
                            {mine.length > 0 ? (
                              <Button size="small" variant="ghost" onClick={() => unstage(p.extension)}>
                                Cancel changes
                              </Button>
                            ) : (
                              <>
                                <Link className="icon-btn" to={`/t/${encodeURIComponent(tenant.domain)}/view/${encodeURIComponent(p.extension)}`} aria-label={`See ${fullName(p)}’s phone and calls`} title="See what they see">
                                  <Icon name="eye" size={16} />
                                </Link>
                                <button type="button" className="icon-btn" aria-label={`Edit ${fullName(p)}`} title="Edit" disabled={!writable} onClick={() => openDialog({ mode: 'edit', person: p })}>
                                  <Icon name="edit" size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Email login details to ${fullName(p)}`}
                                  title={p.email ? 'Email login details' : 'No email address'}
                                  disabled={!writable || !p.email}
                                  onClick={() => stage({ kind: 'welcome', extension: p.extension, name: fullName(p) })}
                                >
                                  <Icon name="mail" size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Remove ${fullName(p)}`}
                                  title={canRemove ? 'Remove' : 'Removing people is not available yet'}
                                  disabled={!writable || !canRemove}
                                  onClick={() => openDialog({ mode: 'remove', person: p })}
                                >
                                  <Icon name="trash" size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {staged.length > 0 && !reviewing && (
        <div className="tray" role="region" aria-label="Changes to review">
          <div className="grow">
            <strong>{plural(staged.length, 'change')} ready to review</strong>
            <span>Nothing has changed on the phone system yet.</span>
          </div>
          <Button variant="ghost" onClick={() => setStaged([])}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setReviewKey((k) => k + 1);
              setReviewing(true);
            }}
          >
            Review changes
          </Button>
        </div>
      )}

      <PersonDialog
        key={dialogKey}
        state={dialog}
        hasMobile={hasMobile}
        featureSets={featureSets}
        timeZones={timeZones}
        domain={tenant.domain}
        refs={dialog?.mode === 'remove' ? refsOf(dialog.person.extension) : []}
        suggestedExtension={suggestedExtension}
        company={company}
        suggestionHint={lastExtension >= 0 ? `The next number after ${lastExtension}, your last extension. Change it if you like.` : 'A good first extension. Change it if you like.'}
        onClose={() => setDialog(null)}
        taken={(ext) => byExt.has(ext) || adds.some((a) => a.extension === ext) || Boolean(directory.data?.entries.some((e) => e.extension === ext))}
        onAdd={(f, welcome) => {
          stage({ kind: 'add', ...f });
          if (welcome) stage({ kind: 'welcome', extension: f.extension, name: fullName(f) });
          setDialog(null);
        }}
        onEdit={(p, set) => {
          if (Object.keys(set).length === 0) unstage(p.extension, 'edit');
          else stage({ kind: 'edit', extension: p.extension, name: fullName(p), set });
          setDialog(null);
        }}
        onRemove={(p) => {
          stage({ kind: 'remove', extension: p.extension, name: fullName(p) });
          setDialog(null);
        }}
      />

      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={tenant.domain}
        items={staged.map((s) => ({ key: `${s.kind}-${s.extension}`, description: describe(s), intent: toIntent(s) }))}
        onUnstage={(key) => {
          const s = staged.find((x) => `${x.kind}-${x.extension}` === key);
          if (s) unstage(s.extension, s.kind);
        }}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setStaged([]);
            people.reload();
            routing.reload();
            directory.reload();
            reloadTenant();
          }
        }}
      />
    </>
  );
}

// ---- Add / edit / remove dialog -------------------------------------------------------------------

function PersonDialog({
  state,
  hasMobile,
  featureSets,
  timeZones,
  domain,
  refs,
  suggestedExtension,
  suggestionHint,
  company,
  onClose,
  taken,
  onAdd,
  onEdit,
  onRemove
}: {
  state: { mode: 'add' } | { mode: 'edit'; person: Person } | { mode: 'remove'; person: Person } | null;
  hasMobile: boolean;
  featureSets: 'edit' | 'show' | 'hidden';
  timeZones: 'edit' | 'show' | 'hidden';
  domain: string;
  refs: Reference[];
  suggestedExtension: string;
  suggestionHint: string;
  company: CompanyLength;
  onClose: () => void;
  taken: (ext: string) => boolean;
  onAdd: (f: Fields & { extension: string }, welcome: boolean) => void;
  onEdit: (p: Person, set: Partial<Fields>) => void;
  onRemove: (p: Person) => void;
}) {
  const person = state && state.mode !== 'add' ? state.person : null;
  // Initialised once per opening (the parent changes `key`), never overwritten while typing.
  const [f, setF] = useState<Fields & { extension: string }>(() =>
    person
      ? { extension: person.extension, firstName: person.firstName, lastName: person.lastName, email: person.email, mobile: person.mobile, featureSet: person.featureSet }
      : { extension: suggestedExtension, firstName: '', lastName: '', email: '', mobile: '', featureSet: '' }
  );
  const [welcome, setWelcome] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields | 'extension', string>>>({});
  // Their time zone isn't in the list of people: it comes with their own settings, and the
  // choices from the phone system.
  const editing = state?.mode === 'edit' ? state.person.extension : null;
  const wantZones = timeZones !== 'hidden' && Boolean(state) && state?.mode !== 'remove';
  const zones = useData(() => (wantZones ? api.options(domain, 'timezone') : Promise.resolve({ options: [] })), [domain, wantZones]);
  const own = useData(() => (wantZones && editing ? api.account(domain, 'person', editing) : Promise.resolve(null)), [domain, wantZones, editing]);
  const savedZone = own.data?.account.values.timeZone ?? '';
  useEffect(() => {
    for (const o of zones.data?.options ?? []) if (o.value) pbxZoneNames.set(o.value, o.label);
  }, [zones.data]);
  useEffect(() => {
    if (own.data) setF((cur) => (cur.timeZone === undefined ? { ...cur, timeZone: own.data!.account.values.timeZone ?? '' } : cur));
  }, [own.data]);

  if (!state) return null;
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) => setF((cur) => ({ ...cur, [k]: e.target.value }));

  if (state.mode === 'remove') {
    const p = state.person;
    const routes = refs.filter((r) => r.how === 'route');
    const members = refs.filter((r) => r.how === 'member');
    if (routes.length) {
      return (
        <Dialog
          open
          onClose={onClose}
          title={`${fullName(p)} is still in use`}
          subtitle={`Extension ${p.extension}`}
          foot={
            <Button variant="primary" onClick={onClose}>
              OK
            </Button>
          }
        >
          <Notice tone="warn" title="Calls are still sent to them" icon="alert">
            Removing them now would leave these callers with nowhere to go. Send those calls somewhere else first, then remove them.
          </Notice>
          <ul className="used-by">
            {routes.map((r) => (
              <li key={`${r.extension}-${r.field}-${r.where}`}>
                <span>
                  <strong>{r.name}</strong> <span className="muted">({r.where})</span>
                </span>
              </li>
            ))}
          </ul>
        </Dialog>
      );
    }
    return (
      <Dialog
        open
        onClose={onClose}
        title={`Remove ${fullName(p)}?`}
        subtitle={`Extension ${p.extension}`}
        foot={
          <>
            <Button variant="ghost" onClick={onClose}>
              Keep
            </Button>
            <Button variant="danger" icon="trash" onClick={() => onRemove(p)}>
              Add to changes
            </Button>
          </>
        }
      >
        <Notice tone="warn" title="Only partly reversible">
          Undo brings back the extension and its settings, but not its voicemail messages, call history or phone sign-in.
        </Notice>
        {members.length > 0 && <p className="muted">They’ll also be taken out of {describeReferences(members)}.</p>}
        <span className="muted">This is added to your changes. Nothing happens until you review and apply.</span>
      </Dialog>
    );
  }

  const submit = () => {
    const errs = validate(f, state.mode === 'add', company);
    if (state.mode === 'add' && !errs.extension && taken(f.extension.trim())) errs.extension = 'This number is already in use.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const clean = {
      extension: f.extension.trim(),
      firstName: f.firstName.trim(),
      lastName: f.lastName.trim(),
      email: f.email.trim(),
      mobile: f.mobile.trim(),
      featureSet: featureSets === 'edit' ? f.featureSet : (person?.featureSet ?? '')
    };
    const zone = timeZones === 'edit' && f.timeZone !== undefined ? f.timeZone : undefined;
    if (state.mode === 'add') return onAdd({ ...clean, ...(zone ? { timeZone: zone } : {}) }, welcome && !!clean.email);
    const p = state.person;
    const diff: Partial<Fields> = {};
    for (const k of ['firstName', 'lastName', 'email', 'mobile', 'featureSet'] as const) if (clean[k] !== p[k]) diff[k] = clean[k];
    if (zone !== undefined && own.data && zone !== savedZone) diff.timeZone = zone;
    onEdit(p, diff);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={state.mode === 'add' ? 'Add a person' : `Edit ${fullName(state.person)}`}
      subtitle={state.mode === 'add' ? 'They get an extension on your phone system.' : `Extension ${state.person.extension}`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Add to changes
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        noValidate
      >
        {state.mode === 'add' && (
          <Field
            label="Extension"
            value={f.extension}
            onChange={set('extension')}
            error={errors.extension}
            inputMode="numeric"
            autoComplete="off"
            hint={f.extension && f.extension === suggestedExtension ? suggestionHint : extensionHint(company)}
          />
        )}
        <div className="form-grid">
          {/* With the extension already suggested, typing starts at the name. */}
          <Field label="First name" value={f.firstName} onChange={set('firstName')} error={errors.firstName} autoComplete="off" data-autofocus={state.mode === 'add' ? true : undefined} />
          <Field label="Last name" value={f.lastName} onChange={set('lastName')} error={errors.lastName} autoComplete="off" />
        </div>
        <Field label="Email" type="email" value={f.email} onChange={set('email')} error={errors.email} hint="Optional. Used for voicemail and login details." autoComplete="off" />
        {hasMobile && <Field label="Mobile" type="tel" value={f.mobile} onChange={set('mobile')} error={errors.mobile} hint="Optional." autoComplete="off" />}
        {featureSets === 'edit' && (
          <div className="field">
            <span className="field-label">Feature set</span>
            <span className="hint">What this extension can do. Adding someone to a call queue switches them to Call queue agent automatically.</span>
            <div className="radio-cards compact feature-sets" role="radiogroup" aria-label="Feature set">
              {FEATURE_SETS.map((o) => (
                <label className="radio-card" key={o.value || 'standard'}>
                  <input type="radio" name="featureSet" checked={f.featureSet === o.value} onChange={() => setF((cur) => ({ ...cur, featureSet: o.value }))} />
                  <span>
                    <strong>{o.label}</strong>
                    <span className="muted"> {o.description}</span>
                  </span>
                </label>
              ))}
              {f.featureSet && !FEATURE_SETS.some((o) => o.value === f.featureSet) && (
                <label className="radio-card">
                  <input type="radio" checked readOnly />
                  <span>
                    <strong>{ALL_FEATURE_SETS.some((o) => o.value === f.featureSet) ? featureSetLabel(f.featureSet) : 'Set in the phone system'}</strong>
                    <span className="muted"> {ALL_FEATURE_SETS.some((o) => o.value === f.featureSet) ? 'A shared phone’s type. Change it on its Shared phone card.' : f.featureSet}</span>
                  </span>
                </label>
              )}
            </div>
          </div>
        )}
        {featureSets === 'show' && state.mode === 'edit' && (
          <div className="field locked">
            <span className="field-label">Feature set</span>
            <span className="locked-value">
              {featureSetLabel(f.featureSet)}
              <span className="muted">
                <Icon name="lock" size={13} /> Ask Stitch to change it
              </span>
            </span>
          </div>
        )}
        {timeZones === 'edit' && (
          <div className="field">
            <label htmlFor="person-tz">Time zone</label>
            <select
              id="person-tz"
              className="input"
              value={f.timeZone ?? ''}
              disabled={!zones.data || (state.mode === 'edit' && !own.data)}
              onChange={(e) => setF((cur) => ({ ...cur, timeZone: e.target.value }))}
              aria-describedby="person-tz-hint"
            >
              {(zones.data?.options.length ? zones.data.options : [{ value: '', label: 'Default' }]).map((o) => (
                <option key={o.value || 'default'} value={o.value}>
                  {o.value ? o.label : 'The company’s (default)'}
                </option>
              ))}
              {f.timeZone && zones.data && !zones.data.options.some((o) => o.value === f.timeZone) && <option value={f.timeZone}>{f.timeZone}</option>}
            </select>
            <span className="hint" id="person-tz-hint">
              {zones.error || own.error ? 'Their time zone couldn’t be read just now.' : 'For the times on their voicemail, call history and desk phone clock.'}
            </span>
          </div>
        )}
        {timeZones === 'show' && state.mode === 'edit' && (
          <div className="field locked">
            <span className="field-label">Time zone</span>
            <span className="locked-value">
              {own.data ? pbxZoneLabel(savedZone).replace(/^the company’s$/, 'The company’s (default)') : 'Loading…'}
              <span className="muted">
                <Icon name="lock" size={13} /> Ask Stitch to change it
              </span>
            </span>
          </div>
        )}
        {state.mode === 'add' && (
          <label className="check">
            <input type="checkbox" checked={welcome} disabled={!f.email.trim()} onChange={(e) => setWelcome(e.target.checked)} />
            Email them their login details after adding
          </label>
        )}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

export function FeatureSetChip({ value }: { value: string }) {
  if (!value) return <span className="muted">Standard</span>;
  return (
    <Chip small tone={value === 'agent' ? 'brand' : 'neutral'}>
      {featureSetLabel(value)}
    </Chip>
  );
}

/** Many people at once: a spreadsheet, handed to Leland (or to Get set up when Leland is off). */
function ImportButton({ disabled, setupLink }: { disabled: boolean; setupLink: string }) {
  const l = useLeland();
  const picker = useRef<HTMLInputElement>(null);
  if (!l?.enabled)
    return disabled ? null : (
      <Link className="btn ghost" to={setupLink} title="Add people from an Excel or CSV file">
        <Icon name="upload" size={17} strokeWidth={2.2} /> Import
      </Link>
    );
  return (
    <>
      <input
        ref={picker}
        type="file"
        hidden
        accept=".xlsx,.csv,.tsv,.txt,.xls"
        aria-label="Spreadsheet of people to import"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void l.attach(f);
        }}
      />
      <Button variant="ghost" icon="upload" disabled={disabled || l.busy} onClick={() => picker.current?.click()} title="Add people from an Excel or CSV file, with Leland">
        Import
      </Button>
    </>
  );
}

/** Someone's face in the People list: their photo (when they use Stitch and have one), else their initials. */
function ExtensionFace({ tenant, extension, name }: { tenant: string; extension: string; name: string }) {
  const face = usePictureOfExtension(tenant, extension);
  return <Face userId={face?.userId} name={name} size="small" className="person-face" />;
}
